'use client'

import { useState, useCallback } from 'react'
import { cn } from '@/lib/tailwind'
import Image from 'next/image'
import { CheckIcon } from '@phosphor-icons/react'
import type { MediaKind } from '@/entries'
import { ENTRY_PRESENTATION } from '@/entries'
import type { EntryPresentation } from '@/entries'
import { truncateText } from './utils'
import type { AlternativeResult } from './types'

interface AlternativesProps {
  alternatives: AlternativeResult[]
  type: MediaKind
  onSelect?: (alternative: AlternativeResult) => void
  disabled?: boolean
}

/**
 * Alternatives panel showing other possible matches
 */
export function Alternatives({
  alternatives,
  type,
  onSelect,
  disabled = false,
}: AlternativesProps) {
  const [imageErrors, setImageErrors] = useState<Set<string>>(new Set())

  const handleImageError = useCallback((id: string) => {
    setImageErrors((prev) => new Set(prev).add(id))
  }, [])

  // `type` is a `MediaKind`, a subset of `EntryType`, so the record is total over it and
  // the old `|| …movie` fallbacks were unreachable (#390).
  const colors = ENTRY_PRESENTATION[type]
  const Icon = colors.icon

  return (
    <div className="border-t">
      <div className="bg-muted/30 px-3 py-1.5">
        <span className="text-muted-foreground text-[10px] font-medium tracking-wide uppercase">
          Other results
        </span>
      </div>
      <div className="max-h-[200px] divide-y overflow-y-auto">
        {alternatives.map((alt, index) => {
          const altType = (alt.type as MediaKind) || type
          // `alt.type` is an enrichment provider's string, so this index really can miss —
          // unlike `type` above. A miss falls back to the outer kind, as it did before #390.
          const altPresentation: EntryPresentation | undefined =
            ENTRY_PRESENTATION[altType]
          const AltIcon = altPresentation?.icon ?? Icon
          const altColors = altPresentation ?? colors
          const altKey = alt.externalId || `alt-${index}`
          const hasAltImageError = imageErrors.has(altKey)

          return (
            <button
              key={altKey}
              type="button"
              onClick={() => onSelect?.(alt)}
              disabled={disabled || !onSelect}
              className="hover:bg-muted/50 flex w-full items-center gap-3 px-3 py-2.5 text-left transition-colors disabled:opacity-50"
            >
              {/* Thumbnail */}
              <div
                className={cn(
                  'relative h-10 w-10 shrink-0 overflow-hidden rounded',
                  altColors.bg,
                )}
              >
                {alt.imageUrl && !hasAltImageError ? (
                  <Image
                    src={alt.imageUrl}
                    alt={truncateText(alt.name, 30)}
                    fill
                    className="object-cover"
                    sizes="40px"
                    onError={() => handleImageError(altKey)}
                  />
                ) : (
                  <div className="flex h-full items-center justify-center">
                    <AltIcon className={cn('h-4 w-4', altColors.text)} />
                  </div>
                )}
              </div>

              {/* Info */}
              <div className="min-w-0 flex-1">
                <p
                  className="truncate text-sm font-medium"
                  title={alt.name?.length > 40 ? alt.name : undefined}
                >
                  {truncateText(alt.name, 40)}
                </p>
                <div className="flex items-center gap-2 overflow-hidden">
                  {alt.type && alt.type !== type && (
                    <span
                      className={cn('shrink-0 text-[10px]', altColors.text)}
                    >
                      {altPresentation?.label}
                    </span>
                  )}
                  {alt.genres && alt.genres.length > 0 && (
                    <span className="text-muted-foreground truncate text-[10px]">
                      {alt.genres.slice(0, 2).join(', ')}
                    </span>
                  )}
                  {alt.year && (
                    <span className="text-muted-foreground shrink-0 text-[10px]">
                      {alt.year}
                    </span>
                  )}
                </div>
              </div>

              {/* Select indicator */}
              <div className="text-muted-foreground shrink-0">
                <CheckIcon className="h-4 w-4" />
              </div>
            </button>
          )
        })}
      </div>
    </div>
  )
}
